import { SUPPLIERS_QUERY } from "@/apis/gql/apolloClient";
import type { Address, PaymentDetails } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Create = {
  supplierName: string;
  status: string;
  address?: Address;
  paymentDetails?: PaymentDetails;
  isCategory?: boolean;
};

export const useCreateSupplier = (
  {
    type = "creditor",
    config,
  }: {
    type?: "creditor" | "debtor";
    config?: {
      toasts?: {
        loading?: boolean;
      };
    };
  } = {
    type: "creditor",
    config: {
      toasts: {
        loading: true,
      },
    },
  },
): [
  (
    data: Create,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: Create;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [createSupplier, { data, loading, error }] = useMutation(
    CREATE_SUPPLIER_MUTATION,
    {
      refetchQueries: [
        { query: SUPPLIERS_QUERY, variables: { tenantId, type } },
      ],
      awaitRefetchQueries: true,
    },
  );

  const handleCreate = async (
    data: Create,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId =
      config?.toasts?.loading && toast.loading(t("generic_loading"));

    try {
      const response = await createSupplier({
        variables: { newSupplier: { ...data, type } },
      });
      if (response.errors) {
        throw new Error("error creating line item");
      }
      onSuccess();
      toastId && toast.dismiss(toastId);
      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId || undefined,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleCreate, { data, loading, error }];
};

export const CREATE_SUPPLIER_MUTATION = gql`
  mutation CreateSupplier($newSupplier: SupplierInput!) {
    createSupplier(newSupplier: $newSupplier) {
      supplierId
      supplierName
      status
    }
  }
`;
